import React from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import { useShipmentContext } from '../../../components/ShipmentProvider';
import { Link } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ParcelStatusIcon from '../../../components/ParcelStatusIcon';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import Property from '@afosto/app-components-react/Property';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import { parcelFormatter, sizeFormatter } from './formatters';
import translations from './translations';

const ShipmentParcels = () => {
  const intl = useIntl();
  const { shipment = {} } = useShipmentContext();
  const { parcels = [] } = shipment || {};

  const dataSource = useDataGrid(parcels, {
    modules: ['dataProperties'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'number',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.number',
            defaultMessage: 'Number',
            description: 'The column name for column number',
          }),
          type: 'parcel',
        },
        ...(shipment?.is_outbound
          ? [
              {
                key: 'ship_at',
                name: intl.formatMessage({
                  id: 'dataGrid.columns.parcels.shipAt',
                  defaultMessage: 'Ship at',
                  description: 'The column name for column ship at',
                }),
                type: 'datetime',
              },
            ]
          : []),
        {
          key: 'tracking_code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.trackingCode',
            defaultMessage: 'Tracking code',
            description: 'The column name for column tracking code',
          }),
        },
        {
          key: 'tracking_url',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.trackingUrl',
            defaultMessage: 'Tracking url',
            description: 'The column name for column tracking url',
          }),
          type: 'url',
        },
        {
          key: 'is_delivered',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.isDelivered',
            defaultMessage: 'Delivered',
            description: 'The column name for column is delivered',
          }),
          type: 'boolean',
        },
        {
          key: 'size',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.size',
            defaultMessage: 'Size',
            description: 'The column name for column size',
          }),
          type: 'size',
        },
        {
          key: 'weight',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.weight',
            defaultMessage: 'Weight',
            description: 'The column name for column weight',
          }),
        },
        {
          key: 'position',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.position',
            defaultMessage: 'Position',
            description: 'The column name for column position',
          }),
        },
        {
          key: 'created_at',
          name: intl.formatMessage({
            id: 'dataGrid.columns.parcels.createdAt',
            defaultMessage: 'Created at',
            description: 'The column name for column created at',
          }),
          type: 'datetime',
        },
      ],
    },
  });

  return (
    <>
      <Hidden implementation="js" mdUp>
        <Grid container spacing={2}>
          {parcels.length > 0 ? (
            parcels.map(parcel => (
              <Grid item xs={12} key={parcel.id}>
                <Card>
                  <CardActionArea component={Link} to={`/parcels/${parcel.id}/items`}>
                    <Box display="flex" alignItems="center">
                      <Box flex={1}>
                        <ListItem component="div" style={{ paddingBottom: 0 }}>
                          <ListItemText
                            primary={parcel?.number || parcel?.id}
                            primaryTypographyProps={{ color: 'primary' }}
                            secondary={parcel?.tracking_code}
                          />
                        </ListItem>
                        <Box px={2} mb={1}>
                          <Grid container>
                            <Grid item xs={8}>
                              <Property
                                label={intl.formatMessage(translations.parcelCard.shipAt.label)}
                                value={parcel?.ship_at ? formatDateTime(parcel.ship_at) : undefined}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <Property
                                label={intl.formatMessage(translations.parcelCard.position.label)}
                                value={parcel?.position ?? '-'}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                      <Box ml={1} mr={2}>
                        <ParcelStatusIcon parcel={parcel} />
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="subtitle2" align="center" color="textSecondary">
                {intl.formatMessage(translations.emptyMessage)}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{ size: sizeFormatter, parcel: parcelFormatter }}
        />
      </Hidden>
    </>
  );
};

export default ShipmentParcels;
